export interface FaqItem {
  question: string
  answer: string
}

/**
 * Renders visible Q&A as native <details>/<summary> (semantic, works
 * without JS, keyboard-accessible) and returns matching FAQPage JSON-LD
 * via faqJsonLd() below -- kept as one shared data source so the visible
 * content and the structured data can never drift apart (a mismatch
 * between the two is treated as spam by search engines' rich-result
 * guidelines).
 */
import { track } from '../lib/analytics'

export function Faq({ items, title = 'Frequently asked questions' }: { items: FaqItem[]; title?: string }) {
  return (
    <div>
      {title && <h2 className="text-2xl font-semibold text-foreground">{title}</h2>}
      <div className="mt-6 space-y-3">
        {items.map((item, index) => (
          <details
            key={item.question}
            onToggle={(e) => {
              if ((e.currentTarget as HTMLDetailsElement).open) track('faq_opened', { question_index: index })
            }}
            className="group rounded-lg border border-border bg-card p-4 open:shadow-sm"
          >
            <summary className="cursor-pointer list-none text-sm font-semibold text-foreground marker:content-none">
              <span className="flex items-center justify-between gap-3">
                {item.question}
                <span aria-hidden className="shrink-0 text-foreground/40 group-open:rotate-45">
                  +
                </span>
              </span>
            </summary>
            <p className="mt-2 text-sm text-foreground/70">{item.answer}</p>
          </details>
        ))}
      </div>
    </div>
  )
}

export function faqJsonLd(items: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}
